<template>
  <v-container class="py-12">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">Painel financeiro</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
              { text: 'Home', to: '/home' },
              { text: 'Financeiro', disabled: true }
            ]" />
          </div>
          <div class="button-group">
            <v-btn outlined color="primary" elevation="0" to="/financeiro/receitas">
              <v-icon left small>mdi-cash-plus</v-icon>Receitas
            </v-btn>
            <v-btn outlined color="primary" elevation="0" to="/financeiro/dre">
              <v-icon left small>mdi-table-large</v-icon>DRE
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <period-filter :loading="loading" @change="onPeriod" />

    <template v-if="data">
      <div class="stat-grid">
        <stat-card label="Receita líquida" icon="mdi-cash-plus" :value="money(totals.net_revenue)" color="#1450b5"
          :hint="`bruta ${money(totals.gross)} · pedágios ${money(totals.toll)}`" />
        <stat-card label="Custos operacionais" icon="mdi-steering" :value="money(totals.operational_costs)"
          :hint="`${percentOf(totals.operational_costs)} da receita · motoristas, frota`" />
        <stat-card label="Despesas" icon="mdi-receipt-text-outline" :value="money(totals.expenses)"
          :hint="`${percentOf(totals.expenses)} da receita`" />
        <stat-card label="Resultado" icon="mdi-scale-balance" :value="money(totals.result)"
          :color="totals.result < 0 ? '#d32f2f' : '#2e7d32'"
          :hint="totals.margin_percent !== null ? `margem de ${percent(totals.margin_percent)}` : 'sem receita no período'" />
      </div>

      <section class="section-grid">
        <v-card class="section-card section-grid__wide">
          <v-card-title>
            <div>Receita, custos e resultado<small>Por mês de competência</small></div>
          </v-card-title>
          <v-card-text>
            <apexchart type="line" height="320" :options="monthlyChart.options" :series="monthlyChart.series" />
          </v-card-text>
        </v-card>

        <v-card class="section-card">
          <v-card-title>
            <div>Para onde vai o dinheiro<small>Custos e despesas do período</small></div>
          </v-card-title>
          <v-card-text>
            <div v-if="!data.composition.length" class="body-2 grey--text py-6 text-center">Sem custos no período.</div>
            <div v-for="item in data.composition" :key="item.key" class="bar-row">
              <span class="bar-row__name" :title="item.label">{{ item.label }}</span>
              <div class="bar-row__track"><div :style="{ width: barWidth(item.total, data.composition) + '%' }" /></div>
              <strong>{{ money(item.total) }}</strong>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="section-card">
          <v-card-title>
            <div>Maiores clientes<small>Receita líquida no período</small></div>
            <v-btn small text color="primary" to="/financeiro/receitas">Ver todos</v-btn>
          </v-card-title>
          <v-card-text>
            <div v-if="!data.top_customers.length" class="body-2 grey--text py-6 text-center">Sem viagens finalizadas no período.</div>
            <div v-for="item in data.top_customers" :key="String(item.customer_id)" class="bar-row">
              <span class="bar-row__name" :title="item.name">{{ item.name }}</span>
              <div class="bar-row__track"><div class="bar-row__fill--revenue" :style="{ width: item.share + '%' }" /></div>
              <strong>{{ money(item.net) }}</strong>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="section-card section-grid__wide">
          <v-card-title>
            <div>A pagar<small>Situação de hoje, independente do período</small></div>
          </v-card-title>
          <v-card-text>
            <div class="payables">
              <router-link to="/financeiro/despesas" class="payable" :class="{ 'payable--alert': payables.expenses_overdue.count }">
                <small>Despesas vencidas</small>
                <strong>{{ money(payables.expenses_overdue.total) }}</strong>
                <span>{{ payables.expenses_overdue.count }} lançamento(s)</span>
              </router-link>
              <router-link to="/financeiro/despesas" class="payable">
                <small>Despesas nos próximos 7 dias</small>
                <strong>{{ money(payables.expenses_next_7_days.total) }}</strong>
                <span>{{ payables.expenses_next_7_days.count }} lançamento(s)</span>
              </router-link>
              <router-link to="/financeiro/motoristas" class="payable">
                <small>Fechamentos de motoristas a pagar</small>
                <strong>{{ money(payables.driver_payouts.total) }}</strong>
                <span>{{ payables.driver_payouts.count }} fechamento(s) aberto(s) ou conferido(s)</span>
              </router-link>
            </div>
          </v-card-text>
        </v-card>
      </section>
    </template>
  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import VueApexCharts from 'vue-apexcharts'
import PeriodFilter from '@/layouts/components/financial/PeriodFilter.vue'

const currency = (value) => 'R$ ' + Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

export default {
  name: 'FinancialDashboard',

  components: {
    apexchart: VueApexCharts,
    PeriodFilter
  },

  data: () => ({
    loading: false,
    data: null
  }),

  computed: {
    totals() {
      return this.data ? this.data.totals : {}
    },

    payables() {
      return this.data.payables
    },

    monthlyChart() {
      const monthly = this.data ? this.data.monthly : []
      return {
        series: [
          { name: 'Receita líquida', type: 'column', data: monthly.map(item => item.net_revenue) },
          { name: 'Custos operacionais', type: 'column', data: monthly.map(item => item.operational_costs) },
          { name: 'Despesas', type: 'column', data: monthly.map(item => item.expenses) },
          { name: 'Resultado', type: 'line', data: monthly.map(item => item.result) }
        ],
        options: {
          chart: { toolbar: { show: false }, fontFamily: 'Poppins, sans-serif' },
          colors: ['#1450b5', '#f0a020', '#8a96ab', '#2e7d32'],
          stroke: { width: [0, 0, 0, 3], curve: 'smooth' },
          markers: { size: [0, 0, 0, 4] },
          plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
          dataLabels: { enabled: false },
          legend: { position: 'top', horizontalAlign: 'left' },
          xaxis: { categories: monthly.map(item => this.monthLabel(item.month)) },
          yaxis: { labels: { formatter: currency } },
          tooltip: { shared: true, intersect: false, y: { formatter: (value) => this.money(value) } },
          grid: { borderColor: '#eef1f6' }
        }
      }
    }
  },

  methods: {
    async onPeriod(range) {
      this.loading = true
      try {
        const response = await axios.get('/api/v2/financial/reports/dashboard', { params: range })
        this.data = response.data.content
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
      } finally {
        this.loading = false
      }
    },

    barWidth(value, list) {
      const max = Math.max(...list.map(item => Number(item.total) || 0), 1)
      return Math.max(Number(value) / max * 100, 2)
    },

    percentOf(value) {
      return this.totals.net_revenue ? this.percent(Number(value) / this.totals.net_revenue * 100) : '-'
    },

    monthLabel(month) {
      const label = moment(`${month}-01`).locale('pt-br').format('MMM/YY')
      return label.charAt(0).toUpperCase() + label.slice(1)
    },

    money(value) {
      return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },

    percent(value) {
      return `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
    }
  }
}
</script>

<style lang="scss" scoped>
.section-card ::v-deep .v-card__text {
  padding-top: 0;
}

.bar-row {
  display: grid;
  grid-template-columns: minmax(120px, 42%) 1fr 110px;
  align-items: center;
  gap: 12px;
  padding: 5px 0;
  font-size: 13px;

  strong {
    text-align: right;
  }
}

.bar-row__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bar-row__track {
  height: 10px;
  border-radius: 5px;
  background: #eef1f6;
  overflow: hidden;

  div {
    height: 100%;
    border-radius: 5px;
    background: #f0a020;
  }

  .bar-row__fill--revenue {
    background: #1450b5;
  }
}

.payables {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.payable {
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  border: 1px solid var(--v-border-base);
  border-radius: 10px;
  text-decoration: none;
  color: inherit;

  &:hover {
    border-color: var(--v-primary-base);
  }

  small {
    color: #52627d;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  strong {
    margin-top: 6px;
    color: #10213f;
    font-size: 20px;
    font-weight: 800;
  }

  span {
    color: #71829d;
    font-size: 11px;
  }

  &--alert {
    border-color: #f5b5b5;
    background: #fff8f8;

    strong {
      color: #d32f2f;
    }
  }
}
</style>
